import { z } from 'zod';

import { MODBUS_PROTOCOL } from 'src/component/UI/commLoopFieldDefs';

export type ConnectionType = 'tcp' | 'serial';

export interface ConnectionFormValues {
  type: ConnectionType;
  tag: string;
  frog_client_id: string;
  ip_address: string;
  port: string;
  serial_port: string;
  custom_serial_port: string;
  baud_rate: string;
  data_bits: string;
  parity: string;
  stop_bits: string;
  protocol: string;
}

export const CUSTOM_SERIAL_PORT = 'custom';

export const SERIAL_PORTS = [
  ...Array.from({ length: 8 }, (_, index) => `COM${index + 1}`),
  '/dev/ttyS0',
  '/dev/ttyS1',
  '/dev/ttyUSB0',
  '/dev/ttyUSB1'
];

export const DEFAULT_CONNECTION_VALUES: ConnectionFormValues = {
  type: 'tcp',
  tag: '',
  frog_client_id: '',
  ip_address: '',
  port: '502',
  serial_port: '',
  custom_serial_port: '',
  baud_rate: '9600',
  data_bits: '8',
  parity: '0',
  stop_bits: '1',
  protocol: String(MODBUS_PROTOCOL.RTU)
};

export const MAX_TEXT_LENGTH = 255;

// Same pattern as the API's validateIpAddress (fox-service-api/src/utils/utils.ts).
const IPV4 = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

const isPort = (value: string) => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 65535;

export function buildConnectionSchema (t: (key: string) => string) {
  const required = () => z.string().trim().min(1, t('common.required'));
  const tooLong = t('deviceNav.addConnection.errors.tooLong');

  const shared = {
    tag: required().max(MAX_TEXT_LENGTH, tooLong),
    frog_client_id: required()
  };

  const tcp = z.object({
    ...shared,
    type: z.literal('tcp'),
    ip_address: required().regex(IPV4, t('deviceNav.addConnection.errors.ipAddress')),
    port: required().refine(isPort, t('deviceNav.addConnection.errors.port'))
  });

  const serial = z.object({
    ...shared,
    type: z.literal('serial'),
    serial_port: required(),
    custom_serial_port: z.string(),
    baud_rate: required(),
    data_bits: required(),
    parity: required(),
    stop_bits: required(),
    protocol: required()
  });

  return z.discriminatedUnion('type', [tcp, serial]).superRefine((values, ctx) => {
    if (values.type !== 'serial' || values.serial_port !== CUSTOM_SERIAL_PORT) {
      return;
    }
    const customPort = values.custom_serial_port.trim();
    if (!customPort) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['custom_serial_port'], message: t('common.required') });
    } else if (customPort.length > MAX_TEXT_LENGTH) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['custom_serial_port'], message: tooLong });
    }
  });
}

/* The POST /devices/comm-loops body: the active type's fields, as numbers where the API expects them. */
export function toCommLoopPayload (values: ConnectionFormValues) {
  const shared = { tag: values.tag.trim(), frog_client_id: values.frog_client_id };

  if (values.type === 'tcp') {
    return { ...shared, ip_address: values.ip_address.trim(), port: Number(values.port) };
  }

  let serialPort = values.serial_port;
  if (serialPort === CUSTOM_SERIAL_PORT) {
    serialPort = values.custom_serial_port.trim();
  }

  return {
    ...shared,
    serial_port: serialPort,
    baud_rate: Number(values.baud_rate),
    data_bits: Number(values.data_bits),
    parity: Number(values.parity),
    stop_bits: Number(values.stop_bits),
    protocol: Number(values.protocol)
  };
}
